import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { homeService } from "@miplatform/api";
import {
  DocumentPreviewDrawer,
  MiButton,
  MiChip,
  MiDetailPageHeader,
  MiMenu,
  MiModalConfirm,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DASHBOARD_TAB,
  DETAILS_TAB,
  HOME_PHASE,
  HOME_TABS,
  HTTP_STATUSES,
  ICONS_WEB,
  INSPECTIONS_TAB,
  ORDERS_TAB,
  PERMISSIONS,
} from "@miplatform/constants";
import { useBoolean, useRouter } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { OrderActivityDrawer } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Skeleton } from "@mui/material";
import { getHomePhase } from "../../utils/getHomePhase/getHomePhase";
import { WorkOrderAttemptDrawer } from "../NewHomeConstruction/WorkOrderAttemptDrawer/WorkOrderAttemptDrawer";
import { HomeActivityDrawer } from "./ActivityDrawer/HomeActivityDrawer";
import { HomeContactsDrawer } from "./ContactsDrawer/HomeContactsDrawer";
import { HomeDashboardTab } from "./DashboardTab/HomeDashboardTab";
import { HomeDetailsTab } from "./DetailsTab/HomeDetailsTab";
import { HomeDocumentsDrawer } from "./DocumentsDrawer/HomeDocumentsDrawer";
import { HomeInspectionsTab } from "./InspectionsTab/HomeInspectionsTab";
import { HomeOrdersTab } from "./OrdersTab/HomeOrdersTab";
import { useHomeDetailsScreenViewModel } from "./useHomeDetailsScreenViewModel";

const HOME_PHASE_MAP = {
  [HOME_PHASE.UNDER_CONSTRUCTION]: {
    text: "Under Construction",
    color: "grey",
  },
  [HOME_PHASE.UNDER_WARRANTY]: { text: "Under Warranty", color: "success" },
  [HOME_PHASE.OUT_OF_WARRANTY]: { text: "Out of Warranty", color: "error" },
};

const getHomeChip = (home, isLoading) => {
  if (isLoading) {
    return <Skeleton width={110} height={40} />;
  }

  const phase = getHomePhase(home);
  const { text, color } = HOME_PHASE_MAP[phase];
  return (
    <MiChip color={color} label={text} size="small" sx={{ borderRadius: 1 }} />
  );
};

export const HomeDetails = () => {
  const { navigate, params } = useRouter();
  const { id, tab = DASHBOARD_TAB } = params;
  const { classes, theme } = useStyles();

  const { hasPermission } = usePermissions();

  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const {
    home,
    documents,
    selectedDocument,
    contacts,
    inspections,
    orders,
    selectedOrder,
    milestones,
    orderTemplateOptions,
    lastFetchedTimestamp,
    communities,
    attempts,
    selectedWorkOrder,
    getHome,
    getCommunity,
    getCommunities,
    getInspections,
    getOrders,
    getOrderActivity,
    getMilestones,
    getOrderTemplates,
    getHomeIssues,
    getAttempts,
    setSelectedOrder,
    setSelectedWorkOrder,
    setSelectedDocument,
    setLastFetchedTimestamp,
    updateHome,
    isLoading,
    isFetchingOrderDetails,
    handleRefresh,
  } = useHomeDetailsScreenViewModel();

  const [
    isContactsDrawerOpen,
    handleContactsDrawerOpen,
    handleContactsDrawerClose,
  ] = useBoolean();

  const [
    isDocumentsDrawerOpen,
    handleDocumentsDrawerOpen,
    handleDocumentsDrawerClose,
  ] = useBoolean();

  const [
    isActivityDrawerOpen,
    handleActivityDrawerOpen,
    handleActivityDrawerClose,
  ] = useBoolean();

  const [
    isOrderActivityDrawerOpen,
    handleOrderActivityDrawerOpen,
    handleOrderActivityDrawerClose,
  ] = useBoolean();

  const fetchAll = (id) => {
    if (id) {
      getHome(id);
      getOrders(id);
      getInspections(id);
      getHomeIssues(id);
      getAttempts(id);
      getMilestones();
      // Generate timestamp in the format that matches modifiedOn (.NET DateTime)
      const timestamp = new Date()
        .toISOString()
        .replace("Z", "+00:00")
        .replace(/\.(\d{3})/, (match, digits) => `.${digits}0000`);
      setLastFetchedTimestamp(timestamp);
    }
  };

  useEffect(() => {
    fetchAll(id);
    handleActivityDrawerClose();
    handleOrderActivityDrawerClose();
    handleContactsDrawerClose();
    handleDocumentsDrawerClose();
  }, [id]);

  useEffect(() => {
    if (home?.communityId) {
      getCommunity(home.communityId);
    }
    if (home?.builderId) {
      getCommunities(home.builderId);
    }
  }, [home]);

  useEffect(() => {
    getOrderTemplates();
  }, []);

  const refreshHomeData = async () => {
    await getHome(id);
  };

  const refreshInspections = async () => {
    await getInspections(id);
  };

  const handleClickTab = (tab) => {
    navigate(`/homes/${id}/${tab}`);
  };

  const handleViewOrderFromActivity = (activity) => {
    if (!activity.relatedReferenceId) return;
    const match = activity.relatedReferenceId.match(/\/orders\/([^/]+)/);
    const activityOrderId = match ? match[1] : null;
    if (!activityOrderId) return;

    const order = orders.find((order) => order.id === activityOrderId);
    if (order) {
      setSelectedOrder(order);
      handleActivityDrawerClose();
      handleOrderActivityDrawerOpen();
    }
  };

  const headerDrawerButton = (label, onClick) => (
    <MiButton
      variant={BUTTON_VARIANTS.TEXT}
      color={BUTTON_COLORS.BLACK}
      size="small"
      style={{
        maxHeight: 28,
        margin: theme.spacing(1),
        textDecoration: "underline",
        textUnderlineOffset: theme.spacing(1),
      }}
      onClick={onClick}
    >
      {label}
    </MiButton>
  );

  const deleteHome = async () => {
    if (!home) return;
    setDeleteInProgress(true);

    const response = await homeService.homes.delete({
      id,
      modifiedOn: home.modifiedOn,
    });
    setDeleteConfirmModalOpen(false);
    setDeleteInProgress(false);
    if (response.status === HTTP_STATUSES.OK) {
      navigate(`/homes`);
    }
  };

  const openDeleteConfirmModal = () => {
    setDeleteConfirmModalOpen(true);
  };

  const isHomeLoading = !home || isLoading;

  return (
    <Box className={classes.contentContainer}>
      <MiDetailPageHeader
        loading={isHomeLoading}
        title={
          <Box display="flex" alignItems="center" gap={2}>
            {getHomeChip(home, isHomeLoading)}
            {home?.address?.addressLine1}
          </Box>
        }
        subTitles={[
          `${home?.address?.city ?? ""}${
            home?.address?.city && home?.address?.regionCode ? ", " : "--"
          }${home?.address?.regionCode ?? ""}`,
          home?.community ?? "--",
          home?.builder ?? "--",
        ]}
        actionComponent={
          hasPermission(PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION) && (
            <MiMenu
              data={[
                {
                  label: "Delete Home",
                  icon: mdi[ICONS_WEB.DELETE],
                  color: theme.palette.grey.main,
                  action: () => {
                    openDeleteConfirmModal();
                  },
                },
              ]}
            />
          )
        }
      />
      <Box className={classes.tabContainer}>
        <MiTab tabs={HOME_TABS} currentTab={tab} onTabChange={handleClickTab} />
        <Box display="flex" gap={2}>
          {headerDrawerButton("Documents", handleDocumentsDrawerOpen)}
          {headerDrawerButton("Contacts", handleContactsDrawerOpen)}
          {headerDrawerButton("Activity Log", handleActivityDrawerOpen)}
        </Box>
      </Box>
      {/* TAB CONTENT */}
      {tab === DASHBOARD_TAB && (
        <HomeDashboardTab
          home={home}
          orders={orders}
          attempts={attempts}
          fetchAll={() => fetchAll(id)}
          setSelectedOrder={setSelectedOrder}
          setSelectedWorkOrder={setSelectedWorkOrder}
          handleOrderActivityDrawerOpen={handleOrderActivityDrawerOpen}
          lastFetchedTimestamp={lastFetchedTimestamp}
          // don't trigger list's loading state while fetching order details
          isLoading={isHomeLoading && !isFetchingOrderDetails}
        />
      )}
      {tab === DETAILS_TAB && (
        <HomeDetailsTab
          home={home}
          isLoading={isHomeLoading}
          orderTemplateOptions={orderTemplateOptions}
          onSubmit={(data) => updateHome(id, data)}
          communities={communities}
        />
      )}
      {tab === ORDERS_TAB && (
        <HomeOrdersTab
          orders={orders}
          milestones={milestones}
          setSelectedOrder={setSelectedOrder}
          handleOrderActivityDrawerOpen={handleOrderActivityDrawerOpen}
          handleOrderActivityDrawerClose={handleOrderActivityDrawerClose}
          isLoading={isHomeLoading && !isFetchingOrderDetails}
        />
      )}
      {tab === INSPECTIONS_TAB && (
        <HomeInspectionsTab
          inspections={inspections}
          refresh={refreshInspections}
        />
      )}
      <HomeContactsDrawer
        home={home}
        contacts={contacts}
        reload={refreshHomeData}
        open={isContactsDrawerOpen}
        onClose={handleContactsDrawerClose}
      />
      <HomeDocumentsDrawer
        documents={documents}
        selectedDocument={selectedDocument}
        setSelectedDocument={setSelectedDocument}
        loading={isHomeLoading}
        refreshHomeDetails={refreshHomeData}
        open={isDocumentsDrawerOpen}
        onClose={handleDocumentsDrawerClose}
      />
      <DocumentPreviewDrawer
        selectedDocument={selectedDocument}
        onClose={() => setSelectedDocument(null)}
      />
      <HomeActivityDrawer
        home={home}
        open={isActivityDrawerOpen}
        handleViewOrderFromActivity={handleViewOrderFromActivity}
        onClose={handleActivityDrawerClose}
      />
      {selectedWorkOrder && (
        <WorkOrderAttemptDrawer
          open
          isReadOnly
          key={selectedWorkOrder.id}
          listAttempt={selectedWorkOrder}
          onClose={() => setSelectedWorkOrder(null)}
        />
      )}
      <OrderActivityDrawer
        order={selectedOrder ?? {}}
        open={isOrderActivityDrawerOpen}
        onClose={handleOrderActivityDrawerClose}
        getOrderActivity={getOrderActivity}
        onRefresh={() => handleRefresh(id)}
      />
      <MiModalConfirm
        titleIcon={ICONS_WEB.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        title="Confirm Delete"
        description="Are you sure you want to remove this home? All data associated with this home will also be deleted."
        open={deleteConfirmModalOpen}
        handlePositive={deleteHome}
        handleNegative={() => setDeleteConfirmModalOpen(false)}
        className={classes.modal}
        disableClick={deleteInProgress}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "inherit",
  },
  tabContainer: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: theme.palette.white.main,
    paddingRight: theme.spacing(2),
  },
}));
